import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang } from "@/context/LanguageContext";

const IMAGES = [
  "https://images.pexels.com/photos/34221129/pexels-photo-34221129.jpeg",
  "https://images.pexels.com/photos/37793325/pexels-photo-37793325.jpeg",
  "https://images.unsplash.com/photo-1612544409025-e1f6a56c1152",
  "https://images.unsplash.com/photo-1783700085825-1df197a49f40",
  "https://images.pexels.com/photos/9121155/pexels-photo-9121155.jpeg",
  "https://images.unsplash.com/photo-1637250096679-c10f2751def8",
];

function PortfolioCard({ item, image, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--x", `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty("--y", `${e.clientY - rect.top}px`);
  };

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={
        index % 5 === 0
          ? "md:col-span-7 md:col-start-1"
          : index % 5 === 1
          ? "md:col-span-4 md:col-start-9"
          : index % 5 === 2
          ? "md:col-span-5 md:col-start-2"
          : index % 5 === 3
          ? "md:col-span-6 md:col-start-7"
          : "md:col-span-6 md:col-start-1"
      }
      data-testid={`portfolio-card-${index}`}
    >
      <div
        className="spotlight aspect-[4/5] relative"
        onMouseMove={handleMouseMove}
        data-cursor="hover"
      >
        <motion.img
          src={image}
          alt={item.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ y }}
        />
      </div>
      <div className="mt-6 flex items-baseline justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-[color:var(--muted)] mb-2">
            {item.tag} · {item.year}
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-bold tracking-[-0.02em]">
            {item.title}
          </h3>
          <div className="text-sm text-[color:var(--muted)] mt-1 font-serif-italic">
            {item.client}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Portfolio() {
  const { t } = useLang();
  return (
    <section
      id="work"
      className="relative py-32 md:py-48 px-6 md:px-12 bg-[#050505] border-t hairline"
      data-testid="portfolio-section"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20 items-end">
          <div className="md:col-span-8">
            <div className="flex items-center gap-4 mb-8">
              <span className="inline-block w-8 h-px bg-[color:var(--accent)]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[color:var(--muted)]">
                {t.portfolio.label}
              </span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display uppercase text-5xl md:text-7xl lg:text-8xl font-extrabold leading-[0.9] tracking-[-0.03em]"
            >
              {t.portfolio.title}
            </motion.h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-24 md:gap-x-8">
          {t.portfolio.items.map((item, i) => (
            <PortfolioCard key={i} item={item} image={IMAGES[i % IMAGES.length]} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
